import _ from "lodash";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import RefactoredFacilityTableHeaderFilter from "./RefactoredFacilityTableHeaderFilter";
import RefactoredFacilityTableItemView from "./RefactoredFacilityTableItemView";
import {
  ColumnId,
  FacilityFilterFormData,
  RefactoredFacilityTableCommonProps,
  SortCondition,
  SortOrder,
  SortableField,
} from "./types";
import { HEALTH_INDEX_OPTIONS, ORDER } from "../../../config";
import useLabels from "../../../containers/FacilityDetailsPage/hooks/useLabels";
import { useCategories, useLocationMunis, useMe } from "../../../hooks/queries";
import { AccountType } from "../../../models/AccountData";
import { createDefaultConditionFormData } from "../../../models/FacilityFilterConditionData";
import { LocationData } from "../../../models/LocationData";
import { convertToGroupData } from "../../../models/MunicipalityData";
import LocalStorageSvc from "../../../services/localStorageSvc";
import ConfigurableTableLayout from "../../Common/ConfigurableTableLayout";

import "./styles.scss";

const ALL_SORTABLE_FIELDS: SortableField[] = [
  "name",
  "stats.score",
  "average_rating",
  "total_review_count",
  "stats.profile_views",
  "stats.profile_views_change_percentage",
  "stats.direction_search_count",
  "stats.direction_search_count_change_percentage",
  "prefecture",
  "city",
  "category",
  "labels",
];

const DEFAULT_VISIBLE_COLUMNS: ColumnId[] = [
  "name",
  "average_rating",
  "total_review_count",
  "health_index",
  "prefecture",
  "city",
  "category",
  "labels",
  "actions",
];

const ALL_COLUMNS: ColumnId[] = [
  "name",
  "average_rating",
  "total_review_count",
  "health_index",
  "profile_views",
  "profile_views_change",
  "direction_search",
  "direction_search_change",
  "prefecture",
  "city",
  "category",
  "labels",
  "has_lodging",
  "actions",
];

const VISIBLE_COLUMNS_STORAGE_KEY = "FacilityListPage_RefactoredVisibleColumns";
const CITY_FILTER_KEYWORD_STORAGE_KEY =
  "FacilityListPage_RefactoredCityFilterKeyword";
const PREFECTURE_FILTER_KEYWORD_STORAGE_KEY =
  "FacilityListPage_RefactoredPrefectureFilterKeyword";

const keepPrefectureBeforeCity = (columns: ColumnId[]): ColumnId[] => {
  if (!columns.includes("prefecture") || !columns.includes("city")) {
    return columns;
  }

  const withoutPrefecture = columns.filter((col) => col !== "prefecture");
  const cityIndex = withoutPrefecture.indexOf("city");

  return [
    ...withoutPrefecture.slice(0, cityIndex),
    "prefecture",
    ...withoutPrefecture.slice(cityIndex),
  ];
};

const keepActionsAsRightmost = (columns: ColumnId[]): ColumnId[] => {
  if (!columns.includes("actions")) {
    return columns;
  }

  return [...columns.filter((col) => col !== "actions"), "actions"];
};

const normalizeVisibleColumns = (value: unknown): ColumnId[] => {
  if (!Array.isArray(value)) {
    return keepActionsAsRightmost(
      keepPrefectureBeforeCity(DEFAULT_VISIBLE_COLUMNS)
    );
  }

  const filtered = value.filter((col): col is ColumnId =>
    ALL_COLUMNS.includes(col as ColumnId)
  );
  const unique = Array.from(new Set(filtered));

  if (unique.length === 0) {
    return keepActionsAsRightmost(
      keepPrefectureBeforeCity(DEFAULT_VISIBLE_COLUMNS)
    );
  }

  let withRequiredColumns: ColumnId[] = unique.includes("name")
    ? unique
    : ["name", ...unique];

  if (!withRequiredColumns.includes("actions")) {
    withRequiredColumns = [...withRequiredColumns, "actions"];
  }

  return keepActionsAsRightmost(keepPrefectureBeforeCity(withRequiredColumns));
};

const getSortLabelKey = (field: SortableField) => {
  switch (field) {
    case "name":
      return "facility_name";
    case "stats.score":
      return "health_index";
    case "average_rating":
      return "average_rating";
    case "total_review_count":
      return "total_review_count";
    case "stats.profile_views":
      return "profile_views";
    case "stats.profile_views_change_percentage":
      return "profile_views_change_percentage";
    case "stats.direction_search_count":
      return "direction_search_count";
    case "stats.direction_search_count_change_percentage":
      return "direction_search_count_change_percentage";
    case "prefecture":
      return "prefecture";
    case "city":
      return "city";
    case "category":
      return "category";
    case "labels":
      return "labels";
    default:
      return "facility_name";
  }
};

const getSortConditionLabel = (
  field: SortableField,
  t: (key: string) => string
) => {
  if (field === "prefecture") {
    return "都道府県";
  }
  if (field === "city") {
    return "市区町村";
  }
  return t(getSortLabelKey(field));
};

type ActiveFilterCondition = {
  id: string;
  label: string;
  kind:
    | "health"
    | "city"
    | "prefecture"
    | "city_text"
    | "prefecture_text"
    | "prof_boolean"
    | "category"
    | "label"
    | "lodging";
  value: string | number | boolean;
};

const RefactoredFacilityTable: React.FunctionComponent<
  RefactoredFacilityTableCommonProps
> = ({
  dataList,
  totalCount,
  filterFormData,
  conditionFormData,
  organizationId,
  onChangeFilterFormData,
  onApplyFilterCondition,
  onSortConditionsChange,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityListPage.facilityList.${key}`);
  const tFilter = useCallback(
    (key: string) => _t(`facilityListPage.modalFilterCondition.${key}`),
    [_t]
  );
  const { data: me } = useMe();

  const [visibleColumns, setVisibleColumns] = useState<ColumnId[]>(() => {
    const savedColumns = LocalStorageSvc.getItem<unknown>(
      VISIBLE_COLUMNS_STORAGE_KEY,
      z.array(z.string()),
      DEFAULT_VISIBLE_COLUMNS
    );
    return normalizeVisibleColumns(savedColumns);
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [cityFilterKeyword, setCityFilterKeyword] = useState<string>(() => {
    return (
      LocalStorageSvc.getItem<string>(
        CITY_FILTER_KEYWORD_STORAGE_KEY,
        z.string(),
        ""
      ) || ""
    );
  });
  const [prefectureFilterKeyword, setPrefectureFilterKeyword] =
    useState<string>(() => {
      return (
        LocalStorageSvc.getItem<string>(
          PREFECTURE_FILTER_KEYWORD_STORAGE_KEY,
          z.string(),
          ""
        ) || ""
      );
    });

  const updateCityFilterKeyword = useCallback((nextKeyword: string) => {
    setCityFilterKeyword(nextKeyword);
    LocalStorageSvc.setItem(CITY_FILTER_KEYWORD_STORAGE_KEY, nextKeyword);
  }, []);

  const updatePrefectureFilterKeyword = useCallback((nextKeyword: string) => {
    setPrefectureFilterKeyword(nextKeyword);
    LocalStorageSvc.setItem(PREFECTURE_FILTER_KEYWORD_STORAGE_KEY, nextKeyword);
  }, []);

  useEffect(() => {
    LocalStorageSvc.setItem(VISIBLE_COLUMNS_STORAGE_KEY, visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    LocalStorageSvc.setItem(CITY_FILTER_KEYWORD_STORAGE_KEY, cityFilterKeyword);
  }, [cityFilterKeyword]);

  useEffect(() => {
    LocalStorageSvc.setItem(
      PREFECTURE_FILTER_KEYWORD_STORAGE_KEY,
      prefectureFilterKeyword
    );
  }, [prefectureFilterKeyword]);

  const isOrgUser = useMemo(
    () => me?.type !== AccountType.LOC_ADMIN,
    [me?.type]
  );

  // Filters data
  const [healthIndexData, setHealthIndexData] = useState<any[]>([]);

  useEffect(() => {
    const health = _.cloneDeep(HEALTH_INDEX_OPTIONS);
    health.forEach((item) => {
      item.name = tFilter(`healthIndexLabels.${item.key}`);
    });
    _.intersectionBy(health, conditionFormData.healthIndex, "name").forEach(
      (item) => (item.checked = true)
    );
    setHealthIndexData(health);
  }, [conditionFormData.healthIndex, tFilter]);

  // Load Filters data directly using hooks for smooth column-level updates
  const { data: categories } = useCategories();
  const { data: municipalities } = useLocationMunis(
    me?.id,
    organizationId || ""
  );

  const [categoriesData, setCategoriesData] = useState<any[]>([]);

  useEffect(() => {
    if (categories) {
      const mapped = categories.map((cat: any) => ({
        ...cat,
        checked:
          conditionFormData.categories?.some((c) => c.id === cat.id) || false,
      }));
      setCategoriesData(mapped);
    }
  }, [categories, conditionFormData.categories]);

  const { data: labels } = useLabels(
    _.pickBy({
      organization_id: organizationId,
    })
  );

  const labelsData = useMemo(
    () =>
      (labels || []).map((label) => ({
        ...label,
        checked:
          conditionFormData.labels?.some((l) => l.id === label.id) || false,
      })),
    [labels, conditionFormData.labels]
  );

  const sortConditions = useMemo<SortCondition[]>(() => {
    const currentField = ALL_SORTABLE_FIELDS.includes(
      filterFormData.sort as SortableField
    )
      ? (filterFormData.sort as SortableField)
      : ("name" as SortableField);

    return [
      {
        field: currentField,
        order:
          filterFormData.order === ORDER.DESC
            ? (ORDER.DESC as SortOrder)
            : (ORDER.ASC as SortOrder),
      },
    ];
  }, [filterFormData.order, filterFormData.sort]);

  const notifySortConditions = (
    nextFilterFormData: FacilityFilterFormData,
    nextSortConditions: SortCondition[]
  ) => {
    onSortConditionsChange?.({
      filterFormData: nextFilterFormData,
      sortConditions: nextSortConditions,
    });
  };

  const applySortField = (
    field: SortableField,
    order: SortOrder,
    nextSelected: SortableField[]
  ) => {
    const nextFilterFormData = {
      ...filterFormData,
      sort: field,
      order,
      sortBy: `${t(getSortLabelKey(field))} ${order === "desc" ? "↓" : "↑"}`,
      pageIndex: 1,
    };

    onChangeFilterFormData(nextFilterFormData);

    notifySortConditions(
      nextFilterFormData,
      nextSelected.map((item) => ({
        field: item,
        order: item === field ? order : (ORDER.ASC as SortOrder),
      }))
    );
  };

  const onSelectSortField = (field: SortableField) => {
    const nextOrder: SortOrder =
      filterFormData.sort === field && filterFormData.order === ORDER.ASC
        ? (ORDER.DESC as SortOrder)
        : (ORDER.ASC as SortOrder);

    applySortField(field, nextOrder, [field]);
  };

  const onRemoveSortField = (field: SortableField) => {
    if (field === "name") {
      applySortField("name", ORDER.DESC as SortOrder, ["name"]);
      return;
    }

    applySortField("name", ORDER.DESC as SortOrder, ["name"]);
  };

  const currentSortField = ALL_SORTABLE_FIELDS.includes(
    filterFormData.sort as SortableField
  )
    ? (filterFormData.sort as SortableField)
    : ("name" as SortableField);

  const currentSortOrder = (
    filterFormData.order === ORDER.DESC ? ORDER.DESC : ORDER.ASC
  ) as SortOrder;

  const buildMunicipalityFilterByKeywords = (
    prefectureKeywordValue: string,
    cityKeywordValue: string
  ) => {
    if (!municipalities) {
      return [];
    }

    const prefKeyword = prefectureKeywordValue.trim().toLowerCase();
    const cityKeyword = cityKeywordValue.trim().toLowerCase();

    if (!prefKeyword && !cityKeyword) {
      return [];
    }

    const prefNameByExternalId = new Map<number, string>();
    municipalities.forEach((muni: any) => {
      if (muni.type === "pref") {
        prefNameByExternalId.set(muni.external_id, muni.name || "");
      }
    });

    const updatedMunicipalities = municipalities.map((muni: any) => {
      if (muni.type !== "muni") {
        return { ...muni, checked: false };
      }

      const prefName =
        prefNameByExternalId.get(muni.parent_external_id) ||
        muni.parent_name ||
        "";
      const cityName = muni.name || "";

      const matchesPrefecture =
        prefKeyword.length === 0 ||
        prefName.toLowerCase().includes(prefKeyword);
      const matchesCity =
        cityKeyword.length === 0 ||
        cityName.toLowerCase().includes(cityKeyword);

      return {
        ...muni,
        checked: matchesPrefecture && matchesCity,
      };
    });

    return convertToGroupData(updatedMunicipalities).map((group) => ({
      ...group,
      muniList: group.muniList.filter((muni) => muni.checked),
    }));
  };

  const applyConditionWithTextFilters = (
    nextCondition: typeof conditionFormData,
    nextPrefectureKeyword: string = prefectureFilterKeyword,
    nextCityKeyword: string = cityFilterKeyword
  ) => {
    onApplyFilterCondition(nextCondition, {
      prefecture: nextPrefectureKeyword,
      city: nextCityKeyword,
    });
  };

  // Handle Column Filters Apply & Clear
  const onApplyFilter = (columnId: string, value: any[] | string) => {
    const nextCondition = { ...conditionFormData };

    if (columnId === "health_index") {
      const checkedValues = Array.isArray(value) ? value : [];
      const healthOpts = HEALTH_INDEX_OPTIONS.map((opt) => {
        const displayLabel = tFilter(`healthIndexLabels.${opt.key}`);
        const checked = checkedValues.includes(opt.key);
        return {
          key: opt.key,
          name: displayLabel,
          checked,
        };
      });
      nextCondition.healthIndex = healthOpts;
    } else if (columnId === "prefecture") {
      const nextPrefectureKeyword = typeof value === "string" ? value : "";
      updatePrefectureFilterKeyword(nextPrefectureKeyword);
      nextCondition.municipalities = buildMunicipalityFilterByKeywords(
        nextPrefectureKeyword,
        cityFilterKeyword
      );
    } else if (columnId === "city") {
      const nextCityKeyword = typeof value === "string" ? value : "";
      updateCityFilterKeyword(nextCityKeyword);
      nextCondition.municipalities = buildMunicipalityFilterByKeywords(
        prefectureFilterKeyword,
        nextCityKeyword
      );
    } else if (columnId === "category") {
      const checkedValues = Array.isArray(value) ? value : [];
      if (categories) {
        nextCondition.categories = categories
          .filter((cat: any) => checkedValues.includes(cat.id))
          .map((cat: any) => ({ ...cat, checked: true }));
      }
    } else if (columnId === "labels") {
      const checkedValues = Array.isArray(value) ? value : [];
      nextCondition.labels = (labels || [])
        .filter((label) => checkedValues.includes(label.id))
        .map((label) => ({ ...label, checked: true }));
    }

    onChangeFilterFormData({
      ...filterFormData,
      pageIndex: 1,
    });
    if (columnId === "prefecture") {
      const nextPrefectureKeyword = typeof value === "string" ? value : "";
      applyConditionWithTextFilters(
        nextCondition,
        nextPrefectureKeyword,
        cityFilterKeyword
      );
      return;
    }

    if (columnId === "city") {
      const nextCityKeyword = typeof value === "string" ? value : "";
      applyConditionWithTextFilters(
        nextCondition,
        prefectureFilterKeyword,
        nextCityKeyword
      );
      return;
    }

    applyConditionWithTextFilters(nextCondition);
  };

  const onClearFilter = (columnId: string) => {
    const nextCondition = { ...conditionFormData };

    if (columnId === "health_index") {
      nextCondition.healthIndex = [];
    } else if (columnId === "prefecture") {
      updatePrefectureFilterKeyword("");
      nextCondition.municipalities = buildMunicipalityFilterByKeywords(
        "",
        cityFilterKeyword
      );
    } else if (columnId === "city") {
      updateCityFilterKeyword("");
      nextCondition.municipalities = buildMunicipalityFilterByKeywords(
        prefectureFilterKeyword,
        ""
      );
    } else if (columnId === "category") {
      nextCondition.categories = [];
    } else if (columnId === "labels") {
      nextCondition.labels = [];
    }

    onChangeFilterFormData({
      ...filterFormData,
      pageIndex: 1,
    });
    if (columnId === "prefecture") {
      applyConditionWithTextFilters(nextCondition, "", cityFilterKeyword);
      return;
    }

    if (columnId === "city") {
      applyConditionWithTextFilters(nextCondition, prefectureFilterKeyword, "");
      return;
    }

    applyConditionWithTextFilters(nextCondition);
  };

  const getTips = (score: string, item: LocationData) => {
    if (!score) {
      return "";
    }
    if (me) {
      if (me.type === AccountType.ORG_ADMIN || me.type === AccountType.ADMIN) {
        return t(
          `tipsTexts${
            item.has_lodging ? "OrgAdminHotel" : "OrgAdmin"
          }.${score.toUpperCase()}`
        );
      }
      if (me.type === AccountType.LOC_ADMIN) {
        return t(
          `tipsTexts${
            item.has_lodging ? "LocAdminHotel" : "LocAdmin"
          }.${score.toUpperCase()}`
        );
      }
    }
    return "";
  };

  // Toggle Columns visibility helper
  const toggleColumnVisibility = (columnId: ColumnId) => {
    if (columnId === "name" || columnId === "actions") {
      return; // facility name and actions are required
    }
    setVisibleColumns((prev) => {
      if (prev.includes(columnId)) {
        return normalizeVisibleColumns(prev.filter((col) => col !== columnId));
      }
      return normalizeVisibleColumns([...prev, columnId]);
    });
  };

  // Dynamic CSS Grid Layout calculation based on visible columns
  const getDynamicGridTemplateStyle = () => {
    // Fixed widths so columns never resize as pagination swaps in rows with
    // different content lengths (a `minmax(max-content, ...)` track is
    // recalculated from whatever rows are currently mounted, so it would
    // change size on every page). Content wider than the column truncates
    // with "..." + a title tooltip; the table scrolls horizontally instead
    // of squishing columns. `name` gets a fixed floor plus `1fr` so it
    // absorbs any leftover container width instead of leaving the table
    // looking cut short when there aren't enough columns to fill the row
    // (the floor is a constant, not content-derived, so this still can't
    // resize between pages).
    const colWidths: Record<ColumnId, string> = {
      name: "minmax(280px, 1fr)",
      average_rating: "130px",
      total_review_count: "110px",
      health_index: "120px",
      profile_views: "110px",
      profile_views_change: "150px",
      direction_search: "150px",
      direction_search_change: "170px",
      prefecture: "120px",
      city: "140px",
      category: "200px",
      labels: "160px",
      has_lodging: "120px",
      actions: "96px",
    };

    const widths = visibleColumns.map((colId) => colWidths[colId] || "160px");
    return {
      gridTemplateColumns: widths.join(" "),
    };
  };

  const getColumnLabelText = (columnId: ColumnId) => {
    switch (columnId) {
      case "name":
        return t("facility_name");
      case "average_rating":
        return t("average_rating");
      case "total_review_count":
        return t("total_review_count");
      case "health_index":
        return t("health_index");
      case "profile_views":
        return t("profile_views");
      case "profile_views_change":
        return `${t("profile_views")} 增減率`;
      case "direction_search":
        return t("direction_search_count");
      case "direction_search_change":
        return `${t("direction_search_count")} 增減率`;
      case "prefecture":
        return t("prefecture");
      case "city":
        return t("city");
      case "category":
        return t("category");
      case "labels":
        return t("labels");
      case "has_lodging":
        return t("has_lodging");
      case "actions":
        return t("actions");
    }
  };

  const activeFilterConditions = useMemo<ActiveFilterCondition[]>(() => {
    const results: ActiveFilterCondition[] = [];

    (conditionFormData.healthIndex || [])
      .filter((item) => item.checked)
      .forEach((item) => {
        results.push({
          id: `health-${item.key}`,
          label: `健康指標: ${item.name}`,
          kind: "health",
          value: item.key,
        });
      });

    const hasPrefectureKeyword = prefectureFilterKeyword.trim().length > 0;
    const hasCityKeyword = cityFilterKeyword.trim().length > 0;

    if (hasPrefectureKeyword) {
      results.push({
        id: "prefecture-text",
        label: `都道府県: ${prefectureFilterKeyword}`,
        kind: "prefecture_text",
        value: prefectureFilterKeyword,
      });
    }

    if (hasCityKeyword) {
      results.push({
        id: "city-text",
        label: `市区町村: ${cityFilterKeyword}`,
        kind: "city_text",
        value: cityFilterKeyword,
      });
    }

    // Fallback for non-text municipality filters from legacy flows.
    if (!hasPrefectureKeyword && !hasCityKeyword) {
      (conditionFormData.municipalities || []).forEach((group) => {
        if (group.muniList.length > 0) {
          results.push({
            id: `prefecture-${group.name}`,
            label: `都道府県: ${group.name}`,
            kind: "prefecture",
            value: group.name,
          });
        }

        group.muniList.forEach((muni) => {
          results.push({
            id: `city-${muni.id}`,
            label: `市区町村: ${muni.name}`,
            kind: "city",
            value: muni.id,
          });
        });
      });
    }

    (conditionFormData.categories || []).forEach((item) => {
      results.push({
        id: `category-${item.id}`,
        label: `業種: ${item.name}`,
        kind: "category",
        value: item.id,
      });
    });

    (conditionFormData.labels || []).forEach((item) => {
      results.push({
        id: `label-${item.id}`,
        label: `ラベル: ${item.name}`,
        kind: "label",
        value: item.id,
      });
    });

    if (conditionFormData.hasLodging?.shouldInclude === true) {
      results.push({
        id: "lodging-true",
        label: `宿泊施設: ${tFilter("lodgingLabels.hotel_facilities")}`,
        kind: "lodging",
        value: true,
      });
    }
    if (conditionFormData.hasLodging?.shouldInclude === false) {
      results.push({
        id: "lodging-false",
        label: `宿泊施設: ${tFilter("lodgingLabels.other_facilities")}`,
        kind: "lodging",
        value: false,
      });
    }

    (conditionFormData.profBooleans || []).forEach((item) => {
      if (item.shouldInclude === undefined) {
        return;
      }

      const stateLabel = tFilter(
        `profBoolLabels.${item.key}.${item.shouldInclude ? "true" : "false"}`
      );
      const conditionLabel = tFilter(`profBoolLabels.${item.key}.label`);

      results.push({
        id: `prof-boolean-${item.key}-${item.shouldInclude ? "true" : "false"}`,
        label: `施設の状態: ${conditionLabel} (${stateLabel})`,
        kind: "prof_boolean",
        value: item.key,
      });
    });

    return results;
  }, [conditionFormData, tFilter, prefectureFilterKeyword, cityFilterKeyword]);

  const onRemoveActiveFilterCondition = (condition: ActiveFilterCondition) => {
    const nextCondition = _.cloneDeep(conditionFormData);

    if (condition.kind === "health") {
      nextCondition.healthIndex = (nextCondition.healthIndex || []).filter(
        (item) => item.key !== condition.value
      );
    }

    if (condition.kind === "city") {
      nextCondition.municipalities = (nextCondition.municipalities || []).map(
        (group) => ({
          ...group,
          muniList: group.muniList.filter(
            (muni) => muni.id !== condition.value
          ),
        })
      );
    }

    if (condition.kind === "prefecture") {
      nextCondition.municipalities = (nextCondition.municipalities || []).map(
        (group) => {
          if (group.name === condition.value) {
            return {
              ...group,
              muniList: [],
            };
          }
          return group;
        }
      );
    }

    if (condition.kind === "prefecture_text") {
      const nextPrefectureKeyword = "";
      updatePrefectureFilterKeyword(nextPrefectureKeyword);
      nextCondition.municipalities = buildMunicipalityFilterByKeywords(
        nextPrefectureKeyword,
        cityFilterKeyword
      );

      onChangeFilterFormData({
        ...filterFormData,
        pageIndex: 1,
      });
      applyConditionWithTextFilters(
        nextCondition,
        nextPrefectureKeyword,
        cityFilterKeyword
      );
      return;
    }

    if (condition.kind === "city_text") {
      const nextCityKeyword = "";
      updateCityFilterKeyword(nextCityKeyword);
      nextCondition.municipalities = buildMunicipalityFilterByKeywords(
        prefectureFilterKeyword,
        nextCityKeyword
      );

      onChangeFilterFormData({
        ...filterFormData,
        pageIndex: 1,
      });
      applyConditionWithTextFilters(
        nextCondition,
        prefectureFilterKeyword,
        nextCityKeyword
      );
      return;
    }

    if (condition.kind === "category") {
      nextCondition.categories = (nextCondition.categories || []).filter(
        (item) => item.id !== condition.value
      );
    }

    if (condition.kind === "label") {
      nextCondition.labels = (nextCondition.labels || []).filter(
        (item) => item.id !== condition.value
      );
    }

    if (condition.kind === "lodging") {
      nextCondition.hasLodging = {
        key: "has_lodging",
        shouldInclude: undefined,
      };
    }

    if (condition.kind === "prof_boolean") {
      nextCondition.profBooleans = (nextCondition.profBooleans || []).filter(
        (item) => item.key !== condition.value
      );
    }

    onChangeFilterFormData({
      ...filterFormData,
      pageIndex: 1,
    });
    applyConditionWithTextFilters(nextCondition);
  };

  const onClearAllConditions = () => {
    updateCityFilterKeyword("");
    updatePrefectureFilterKeyword("");
    onApplyFilterCondition(createDefaultConditionFormData(), {
      prefecture: "",
      city: "",
    });
    applySortField("name", ORDER.DESC as SortOrder, ["name"]);
  };

  const uiConditions = useMemo(() => {
    const conditions: Array<
      | {
          id: string;
          label: string;
          kind: "sort";
          sortField: SortableField;
        }
      | {
          id: string;
          label: string;
          kind: "filter";
          condition: ActiveFilterCondition;
        }
    > = [];

    if (sortConditions.length > 0) {
      const latest = sortConditions[0];
      const label = getSortConditionLabel(latest.field, (key: string) =>
        _t(`facilityListPage.facilityList.${key}`)
      );
      conditions.push({
        id: `sort-${latest.field}`,
        label: `並び替え順: ${label} ${
          latest.order === "desc" ? "降順" : "昇順"
        }`,
        kind: "sort",
        sortField: latest.field,
      });
    }

    activeFilterConditions.forEach((condition) => {
      conditions.push({
        id: condition.id,
        label: condition.label,
        kind: "filter",
        condition,
      });
    });

    return conditions;
  }, [activeFilterConditions, sortConditions, _t]);

  return (
    <ConfigurableTableLayout
      className="facility-list-table-layout"
      onContainerClick={() => setShowColumnSettings(false)}
      controls={
        <div
          className="column-settings-container"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn-column-settings"
            onClick={() => setShowColumnSettings((prev) => !prev)}
          >
            表示項目の設定 ⚙️
          </button>
          {showColumnSettings && (
            <div className="column-settings-dropdown">
              <div className="dropdown-title">表示列の切り替え</div>
              {ALL_COLUMNS.map((colId) => (
                <label key={`setting-${colId}`} className="setting-label">
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(colId)}
                    disabled={colId === "name" || colId === "actions"}
                    onChange={() => toggleColumnVisibility(colId)}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="label-text">
                    {getColumnLabelText(colId)}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      }
      conditionChips={uiConditions.map((condition) => ({
        id: condition.id,
        label: condition.label,
        onRemove: () => {
          if (condition.kind === "sort") {
            onRemoveSortField(condition.sortField);
            return;
          }
          onRemoveActiveFilterCondition(condition.condition);
        },
        removeAriaLabel: `remove-${condition.id}`,
      }))}
      onClearAllConditions={onClearAllConditions}
      clearAllLabel={t("refactored.clear_all")}
      tableContent={
        <div
          className="table-data table-data-grid refactored-table-grid"
          style={getDynamicGridTemplateStyle()}
        >
          <RefactoredFacilityTableHeaderFilter
            activeSortField={currentSortField}
            activeSortOrder={currentSortOrder}
            visibleColumns={visibleColumns}
            onSelectSortField={onSelectSortField}
            healthIndexData={healthIndexData}
            categoriesData={categoriesData}
            labelsData={labelsData}
            cityFilterKeyword={cityFilterKeyword}
            prefectureFilterKeyword={prefectureFilterKeyword}
            onApplyFilter={onApplyFilter}
            onClearFilter={onClearFilter}
          />
          {(!dataList || dataList.length === 0) && (
            <div className="row-td text-center sub-txt">{t("no_location")}</div>
          )}
          {dataList.slice(0, filterFormData.numberPerPage).map((item) => (
            <RefactoredFacilityTableItemView
              key={`refactor-${item.id}`}
              item={item}
              organizationId={organizationId}
              isOrgUser={isOrgUser}
              visibleColumns={visibleColumns}
              getTips={getTips}
            />
          ))}
        </div>
      }
      pagination={{
        filterFormData,
        totalRowsNumber: totalCount,
        onChangeFilterFormData: (nextFilterFormData: any) => {
          onChangeFilterFormData(nextFilterFormData);
        },
      }}
    />
  );
};

export default RefactoredFacilityTable;
